/**
 * Tarayıcı üzerinden etiket yazdırma.
 *
 * Etikette sayfa boyutu şablona göre değiştiği için `@page size` dinamik
 * üretilir; sabit A4/A5 stil sabitleri (bkz. `e-commerce/utils/printPageStyles.ts`)
 * burada işe yaramaz.
 *
 * `margin: 0` kritik: tarayıcının varsayılan kenar boşluğu 40 mm etiketi
 * ölçekleyip kaydırır, barkod okunamaz hale gelir.
 */

import { LabelTemplate, A4_WIDTH_MM, A4_HEIGHT_MM } from '../../../types/labelTemplate.types';

export interface PrintSheetSize {
  widthMm: number;
  heightMm: number;
}

export function getPrintSheetSize(template: LabelTemplate): PrintSheetSize {
  const { layout } = template;
  if (layout.mode === 'roll') {
    return { widthMm: template.labelWidthMm, heightMm: template.labelLengthMm };
  }
  if (layout.pageSize === 'A4') return { widthMm: A4_WIDTH_MM, heightMm: A4_HEIGHT_MM };
  if (layout.pageSize === 'A5') return { widthMm: 148, heightMm: 210 };
  return {
    widthMm: layout.pageWidthMm ?? A4_WIDTH_MM,
    heightMm: layout.pageHeightMm ?? A4_HEIGHT_MM,
  };
}

/** react-to-print `pageStyle` prop'una verilecek CSS */
export function buildLabelPageStyle(template: LabelTemplate): string {
  const { widthMm, heightMm } = getPrintSheetSize(template);

  return `
    @page {
      size: ${widthMm}mm ${heightMm}mm;
      margin: 0;
    }
    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: ${widthMm}mm;
        height: auto !important;
        overflow: visible !important;
        background: #ffffff !important;
      }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .label-print-page {
        margin: 0 !important;
        box-shadow: none !important;
        outline: none !important;
      }
      /* Etiket içeriği hiçbir koşulda sayfa ortasında bölünmemeli */
      .label-print-page > * {
        break-inside: avoid;
        page-break-inside: avoid;
      }
    }
  `;
}

/**
 * Hazır HTML'i gizli bir iframe içinde yazdırır.
 * react-to-print kullanılamayan yerlerde (ör. React ağacı dışında üretilmiş
 * içerik) yedek yol.
 */
export function printHtmlInIframe(html: string, pageStyle: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1000);
    };

    const doc = iframe.contentDocument;
    if (!doc) {
      cleanup();
      reject(new Error('Yazdırma çerçevesi oluşturulamadı'));
      return;
    }

    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>${pageStyle}</style></head><body>${html}</body></html>`);
    doc.close();

    // Görseller (barkod/QR data URL) yüklenmeden yazdırılırsa boş çıkar
    const win = iframe.contentWindow;
    if (!win) {
      cleanup();
      reject(new Error('Yazdırma penceresi bulunamadı'));
      return;
    }

    const doPrint = () => {
      try {
        win.focus();
        win.print();
        resolve();
      } catch (err) {
        reject(err instanceof Error ? err : new Error('Yazdırma başarısız'));
      } finally {
        cleanup();
      }
    };

    const images = Array.from(doc.images);
    if (images.length === 0) {
      setTimeout(doPrint, 100);
      return;
    }

    let pending = images.length;
    const onDone = () => {
      pending -= 1;
      if (pending <= 0) setTimeout(doPrint, 120);
    };

    images.forEach((img) => {
      if (img.complete) onDone();
      else {
        img.addEventListener('load', onDone);
        img.addEventListener('error', onDone);
      }
    });

    // Güvenlik ağı: bir görsel hiç yüklenmezse yazdırma askıda kalmasın
    setTimeout(() => {
      if (pending > 0) {
        pending = 0;
        doPrint();
      }
    }, 4000);
  });
}
